/**
 * platform_admin_service.js
 *
 * Service frontend para chamar Cloud Functions do Painel Administrativo Global.
 * Todas as chamadas são via httpsCallable — nunca acessa Firestore diretamente.
 */

import { httpsCallable, getFunctions, connectFunctionsEmulator } from "firebase/functions";
import { getApp } from "firebase/app";
import "./firebase"; // garante que o firebase foi inicializado

// As functions do platform_admin foram publicadas em southamerica-east1
const platformFunctions = getFunctions(getApp(), "southamerica-east1");

if (process.env.REACT_APP_USE_EMULATOR === "true") {
    try {
        connectFunctionsEmulator(platformFunctions, "127.0.0.1", 5001);
    } catch (e) {
        // Ignora erro se já estiver conectado
    }
}

// === Dashboard ===
export const getMetrics = async () => {
    const fn = httpsCallable(platformFunctions, "platformGetMetrics");
    const result = await fn();
    return result.data;
};

// === Tenants ===
export const listTenants = async ({ page = 1, pageSize = 25, filters = {} } = {}) => {
    const fn = httpsCallable(platformFunctions, "platformListTenants");
    const result = await fn({ page, pageSize, filters });
    return result.data;
};

export const getTenantDetail = async (idTenant) => {
    const fn = httpsCallable(platformFunctions, "platformGetTenantDetail");
    const result = await fn({ idTenant });
    return result.data;
};

export const createTenant = async ({ tenant, owner, billing }) => {
    const fn = httpsCallable(platformFunctions, "platformCreateTenant");
    const result = await fn({ tenant, owner, billing });
    return result.data;
};

// === Users ===
export const listUsers = async ({ page = 1, pageSize = 25, filters = {} } = {}) => {
    const fn = httpsCallable(platformFunctions, "platformListUsers");
    const result = await fn({ page, pageSize, filters });
    return result.data;
};

// === Billing / Checkout Sessions ===
export const listCheckoutSessions = async ({ page = 1, pageSize = 25, filters = {} } = {}) => {
    const fn = httpsCallable(platformFunctions, "platformListCheckoutSessions");
    const result = await fn({ page, pageSize, filters });
    return result.data;
};

// === Mercado Pago ===
export const syncMercadoPagoPayment = async (paymentId) => {
    const fn = httpsCallable(platformFunctions, "platformSyncMercadoPagoPayment");
    const result = await fn({ paymentId });
    return result.data;
};

// === Plans ===
export const listPlans = async () => {
    const fn = httpsCallable(platformFunctions, "platformListPlans");
    const result = await fn();
    return result.data;
};

export const savePlan = async (plan) => {
    const fn = httpsCallable(platformFunctions, "platformSavePlan");
    const result = await fn({ plan });
    return result.data;
};

export const deletePlan = async (planId) => {
    const fn = httpsCallable(platformFunctions, "platformDeletePlan");
    const result = await fn({ planId });
    return result.data;
};

// === Audit Logs ===
export const listAuditLogs = async ({ page = 1, pageSize = 25, filters = {} } = {}) => {
    const fn = httpsCallable(platformFunctions, "platformListAuditLogs");
    const result = await fn({ page, pageSize, filters });
    return result.data;
};

// === Usuários Internos ===
export const saveUser = async (userData) => {
    const fn = httpsCallable(platformFunctions, "platformSaveUser");
    const result = await fn(userData);
    return result.data;
};

export const deleteUser = async (uid) => {
    const fn = httpsCallable(platformFunctions, "platformDeleteUser");
    const result = await fn({ uid });
    return result.data;
};

// === Billing ===
export const changeTenantPlan = async (idTenant, newPlanCode) => {
    const fn = httpsCallable(platformFunctions, "platformManageBilling");
    const result = await fn({ action: "changePlan", idTenant, newPlanCode });
    return result.data;
};

export const blockTenantAccess = async (idTenant, reason) => {
    const fn = httpsCallable(platformFunctions, "platformManageBilling");
    const result = await fn({ action: "blockAccess", idTenant, reason });
    return result.data;
};

export const unblockTenantAccess = async (idTenant) => {
    const fn = httpsCallable(platformFunctions, "platformManageBilling");
    const result = await fn({ action: "unblockAccess", idTenant });
    return result.data;
};

export const registerManualPayment = async (idTenant, paymentData) => {
    const fn = httpsCallable(platformFunctions, "platformManageBilling");
    const result = await fn({ action: "registerManualPayment", idTenant, ...paymentData });
    return result.data;
};

export const generateInvoice = async (idTenant, planCode, paymentMethod) => {
    const fn = httpsCallable(platformFunctions, "platformManageBilling");
    const result = await fn({ action: "generateInvoice", idTenant, planCode, paymentMethod });
    return result.data;
};

export const updateTenantTrialPeriod = async (idTenant, trialStartDate, trialEndsAt) => {
    const fn = httpsCallable(platformFunctions, "platformManageBilling");
    const result = await fn({ action: "updateTrialPeriod", idTenant, trialStartDate, trialEndsAt });
    return result.data;
};

// === Suporte ===
export const listSupportTickets = async ({ page = 1, pageSize = 25, filters = {} } = {}) => {
    const fn = httpsCallable(platformFunctions, "platformManageSupport");
    const result = await fn({ action: "listTickets", page, pageSize, filters });
    return result.data;
};

export const saveSupportTicket = async (ticketData) => {
    const fn = httpsCallable(platformFunctions, "platformManageSupport");
    const result = await fn({ action: "saveTicket", ticket: ticketData });
    return result.data;
};

// === Settings ===
export const getSettings = async () => {
    const fn = httpsCallable(platformFunctions, "platformManageSettings");
    const result = await fn({ action: "getSettings" });
    return result.data;
};

export const saveSettings = async (settings) => {
    const fn = httpsCallable(platformFunctions, "platformManageSettings");
    const result = await fn({ action: "saveSettings", settings });
    return result.data;
};

export const getEvolutionStatus = async () => {
    const fn = httpsCallable(platformFunctions, "platformManageSettings");
    const result = await fn({ action: "getEvolutionStatus" });
    return result.data;
};

export const getEvolutionQRCode = async () => {
    const fn = httpsCallable(platformFunctions, "platformManageSettings");
    const result = await fn({ action: "getEvolutionQRCode" });
    return result.data;
};

export const logoutEvolution = async () => {
    const fn = httpsCallable(platformFunctions, "platformManageSettings");
    const result = await fn({ action: "logoutEvolution" });
    return result.data;
};

// === Onboarding ===
export const listOnboardings = async () => {
    const fn = httpsCallable(platformFunctions, "platformManageOnboarding");
    const result = await fn({ action: "listOnboardings" });
    return result.data;
};

export const saveOnboarding = async (onboarding) => {
    const fn = httpsCallable(platformFunctions, "platformManageOnboarding");
    const result = await fn({ action: "saveOnboarding", onboarding });
    return result.data;
};

export const updateOnboardingStep = async (onboardingId, stepId, checked) => {
    const fn = httpsCallable(platformFunctions, "platformManageOnboarding");
    const result = await fn({ action: "updateStep", onboardingId, stepId, checked });
    return result.data;
};

// === Notifications ===
export const listNotificationTemplates = async () => {
    const fn = httpsCallable(platformFunctions, "platformManageNotifications");
    const result = await fn({ action: "listTemplates" });
    return result.data;
};

export const saveNotificationTemplate = async (template) => {
    const fn = httpsCallable(platformFunctions, "platformManageNotifications");
    const result = await fn({ action: "saveTemplate", template });
    return result.data;
};

// === Permissions ===
export const getPermissionsMatrix = async () => {
    const fn = httpsCallable(platformFunctions, "platformManagePermissions");
    const result = await fn({ action: "getMatrix" });
    return result.data;
};

export const savePermissionsMatrix = async (roles) => {
    const fn = httpsCallable(platformFunctions, "platformManagePermissions");
    const result = await fn({ action: "saveMatrix", roles });
    return result.data;
};

const PlatformAdminService = {
    getMetrics,
    listTenants,
    getTenantDetail,
    createTenant,
    listUsers,
    listCheckoutSessions,
    syncMercadoPagoPayment,
    listPlans,
    savePlan,
    deletePlan,
    listAuditLogs,
    saveUser,
    deleteUser,
    changeTenantPlan,
    blockTenantAccess,
    unblockTenantAccess,
    registerManualPayment,
    generateInvoice,
    updateTenantTrialPeriod,
    listSupportTickets,
    saveSupportTicket,
    getSettings,
    saveSettings,
    getEvolutionStatus,
    getEvolutionQRCode,
    logoutEvolution,
    getPermissionsMatrix,
    savePermissionsMatrix,
};

export default PlatformAdminService;
